<template>
  <div :class="{ 'grid-x': isPriceReviewsCombined }">
    <Price
      :price="selectedPrice"
      :name="'price'"
      v-bind="$attrs"
      :class="{ 'cell small-9': isPriceReviewsCombined }"
      :additional-fee
      :is-pricing-simplification="finalPriceDisplayEnabled"
      :surcharge-shipping-message-enabled="surchargeShippingMessageEnabled"
      :is-product-perks-enabled="isProductPerksEnabled"
      :is-top-offer-hidden="isTopOfferHidden"
      :is-ppc-loading="isPpcLoading"
      :is-countdown-enabled="isCountdownEnabled"
      :mocked-price-valid-date="mockPriceDateValue"
    >
      <template
        v-if="(!isEGiftCard && !isProductPerksEnabled) || (!isEGiftCard && isTopOfferHidden && isLoyaltyPromoPresent && isProductPerksEnabled)"
        #ppc
      >
        <Ppc
          :price-id="selectedPrice?.id"
          :price-offer-description="selectedPrice?.offersData.topOffer?.description ?? ''"
          :badges-list="selectedPrice?.badgesList"
        />
      </template>
    </Price>

    <ReviewRating v-if="isPriceReviewsCombined" class="cell small-3 reviewlet" />
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useRoute } from 'vue-router';
import { Price } from '@nuxt-common/media-card-large/app/modules/price/components/bcom';
import ReviewRating from '~/modules/reviews/components/ReviewRating.vue';

import useKillswitches from '~/modules/featureControl/composables/useKillswitches';
import useFeatureEligibility from '~/modules/featureControl/composables/useFeatureEligibility';
import useReviewsStatistics from '~/modules/reviews/composables/useReviewsStatistics';
import useExperiments from '~/stores/useExperimentsStore';
import { useDeviceType } from '~/composables/utils/useDeviceType';
import { useProductType } from '~/composables/useProductType';

import Ppc from '~/modules/ppc/components/PPC.vue';
import { usePpc } from '~/modules/ppc/composables/usePPC';
import { useProductPrice } from '~/modules/price/composables/useProductPrice';

defineOptions({ inheritAttrs: false });

defineProps({
  id: {
    type: [String, Number],
    required: true,
  },
  product: {
    /** @type import('vue').PropType<Product> */
    type: Object,
    required: true,
  },
  currentZipCodeAvailability: {
    type: Object,
    required: false,
    default: null,
  },
  additionalFee: {
    type: Object,
    required: false,
    default: null,
  },
});

const {
  killswitches: { finalPriceDisplayEnabled, surchargeShippingMessageEnabled },
} = useKillswitches();
const { isMobile } = useDeviceType();
const { isPpcLoading, isShowPpc, isPowerPointsBelowPointsThreshold, isLoyaltyPromoPresent } = usePpc();
const { isEGiftCard, isProductPerksEnabled, isReviewletOptimizationEnabled } = useFeatureEligibility();
const selectedPrice = useProductPrice();
const { totalCount } = useReviewsStatistics();
const { isMaster, isShopTheSet } = useProductType();
const {
  experimentFlags: { isCountdownEnabled },
} = useExperiments();
const route = useRoute();

const mockPriceDateValue = ref(null);
if (isCountdownEnabled) {
  mockPriceDateValue.value = route.query.mockPriceDateValue ?? null;
}

const isTopOfferHidden = computed(
  () =>
    isShowPpc.value &&
    !isPowerPointsBelowPointsThreshold.value &&
    (selectedPrice.value?.offersData?.topOffer.isLoyallistOffer || !selectedPrice.value?.offersData?.topOffer?.description),
);

const isPriceReviewsCombined = computed(() => isReviewletOptimizationEnabled && (!isMaster || isShopTheSet) && !!totalCount.value && isMobile.value);
</script>
<style lang="scss" scoped>
:deep(.custom-gray-divider) {
  border-width: 1px 0 0 0;
}

.reviewlet {
  justify-items: end;
}
</style>

<style lang="scss">
.klarna-container {
  ::part(osm-container) {
    font-family:
      Bloomies Text,
      Helvetica Neue,
      Helvetica,
      Arial,
      sans-serif !important;
  }
}
</style>
